import React, { useCallback } from 'react';
import { FieldProps } from 'formik';
import {
  Input as _Input,
  PasswordProps as _PasswordProps,
  TextAreaProps as _TextAreaProps,
  InputRef,
  TextAreaRef,
  InputProps as _InputProps,
  GetRef,
  GetProps,
} from 'custom-test-antd';

import Field from './Field';
import { FormikFieldProps } from './FieldProps';
import { makeField, memoField } from './makeField';

type OTPPropsAntd = GetProps<typeof _Input.Otp>;
type OTPRef = GetRef<typeof _Input.Otp>;

type InputProps = FieldProps & _InputProps;
type PasswordProps = FieldProps & _PasswordProps;
type TextAreaProps = FieldProps & _TextAreaProps;
type OTPProps = FieldProps & OTPPropsAntd;

export { _InputProps as InputProps };
export { _PasswordProps as PasswordProps };
export { _TextAreaProps as TextAreaProps };
export { OTPPropsAntd as OTPProps };

type InputFieldProps = FormikFieldProps & _InputProps;
type PasswordFieldProps = FormikFieldProps & _PasswordProps;
type TextAreaFieldProps = FormikFieldProps & _TextAreaProps;
type OTPFieldProps = FormikFieldProps & OTPPropsAntd;

const InternalInput = React.forwardRef(
  (
    {
      field,
      form,
      meta,
      onChange: _onChange,
      onBlur: _onBlur,
      ...restProps
    }: InputProps,
    ref: React.Ref<InputRef>,
  ) => {
    const {
      value, onChange, onBlur, name,
    } = field;
    return (
      <_Input
        name={name}
        ref={ref}
        value={value}
        onChange={useCallback<NonNullable<_InputProps['onChange']>>((event) => {
          onChange(event);
          if (_onChange) {
            _onChange(event);
          }
        }, [_onChange])}
        onBlur={useCallback<NonNullable<_InputProps['onBlur']>>((event) => {
          onBlur(event);
          if (_onBlur) {
            _onBlur(event);
          }
        }, [])}
        {...restProps}
      />
    );
  },
);

InternalInput.displayName = 'InternalInput';

const InternalInputMemo = memoField(InternalInput);

const InternalInputField = React.forwardRef(
  (
    {
      name,
      validate,
      fast,
      ...restProps
    }: InputFieldProps,
    ref: React.Ref<InputRef>,
  ) => (
    <Field name={name} validate={validate} fast={fast}>
      {(fieldProps: any) => (
        <InternalInputMemo ref={ref} {...fieldProps} {...restProps} />
      )}
    </Field>
  ),
);

InternalInputField.displayName = 'InternalInputField';

const Password = React.forwardRef(
  (
    {
      field,
      form,
      meta,
      onChange: _onChange,
      onBlur: _onBlur,
      ...restProps
    }: PasswordProps,
    ref: React.Ref<InputRef>,
  ) => {
    const {
      value, onChange, onBlur, name,
    } = field;
    return (
      <_Input.Password
        name={name}
        ref={ref}
        value={value}
        onChange={useCallback<NonNullable<_PasswordProps['onChange']>>((event) => {
          onChange(event);
          if (_onChange) {
            _onChange(event);
          }
        }, [_onChange])}
        onBlur={useCallback<NonNullable<_PasswordProps['onBlur']>>((event) => {
          onBlur(event);
          if (_onBlur) {
            _onBlur(event);
          }
        }, [_onBlur])}
        {...restProps}
      />
    );
  },
);

Password.displayName = 'Password';

const PasswordMemo = memoField(Password);

const PasswordFiled = React.forwardRef(
  (
    {
      name,
      validate,
      fast,
      ...restProps
    }: PasswordFieldProps,
    ref: React.Ref<InputRef>,
  ) => (
    <Field name={name} validate={validate} fast={fast}>
      {(fieldProps: any) => (
        <PasswordMemo ref={ref} {...fieldProps} {...restProps} />
      )}
    </Field>
  ),
);

PasswordFiled.displayName = 'PasswordFiled';

const TextArea = React.forwardRef(
  (
    {
      field,
      form,
      meta,
      onChange: _onChange,
      onBlur: _onBlur,
      ...restProps
    }: TextAreaProps,
    ref: React.Ref<TextAreaRef>,
  ) => {
    const {
      value, onChange, onBlur, name,
    } = field;
    return (
      <_Input.TextArea
        name={name}
        ref={ref}
        value={value}
        onChange={useCallback<NonNullable<_TextAreaProps['onBlur']>>((event) => {
          onChange(event);
          if (_onChange) {
            _onChange(event);
          }
        }, [_onChange])}
        onBlur={useCallback<NonNullable<_TextAreaProps['onBlur']>>((event) => {
          onBlur(event);
          if (_onBlur) {
            _onBlur(event);
          }
        }, [_onBlur])}
        {...restProps}
      />
    );
  },
);

TextArea.displayName = 'TextArea';

const TextAreaMemo = memoField(TextArea);

const TextAreaField = React.forwardRef(
  (
    {
      name,
      validate,
      fast,
      ...restProps
    }: TextAreaFieldProps,
    ref: React.Ref<TextAreaRef>,
  ) => (
    <Field name={name} validate={validate} fast={fast}>
      {(fieldProps: any) => (
        <TextAreaMemo ref={ref} {...fieldProps} {...restProps} />
      )}
    </Field>
  ),
);

TextAreaField.displayName = 'TextAreaField';

const Otp = React.forwardRef(
  (
    {
      field,
      form,
      meta,
      onChange: _onChange,
      onBlur: _onBlur,
      ...restProps
    }: OTPProps,
    ref: React.Ref<OTPRef>,
  ) => {
    const {
      value, onBlur, name,
    } = field;
    const { setFieldValue, setFieldTouched } = form;
    return (
      <_Input.Otp
        ref={ref}
        value={value}
        onChange={useCallback<NonNullable<OTPPropsAntd['onChange']>>((inputValue) => {
          setFieldValue(name, inputValue);
          setFieldTouched(name, true, false);
          if (_onChange) {
            _onChange(inputValue);
          }
        }, [_onChange])}
        onBlur={useCallback<NonNullable<OTPPropsAntd['onBlur']>>((event) => {
          onBlur(event);
          if (_onBlur) {
            _onBlur(event);
          }
        }, [_onBlur])}
        {...restProps}
      />
    );
  },
);

Otp.displayName = 'Otp';

const OtpMemo = memoField(Otp);

const OtpField = React.forwardRef(
  (
    {
      name,
      validate,
      fast,
      ...restProps
    }: OTPFieldProps,
    ref: React.Ref<OTPRef>,
  ) => (
    <Field name={name} validate={validate} fast={fast}>
      {(fieldProps: any) => (
        <OtpMemo ref={ref} {...fieldProps} {...restProps} />
      )}
    </Field>
  ),
);

OtpField.displayName = 'OtpField';

export interface InputType
  // eslint-disable-next-line max-len
  extends React.MemoExoticComponent<React.ForwardRefExoticComponent<FieldProps & _InputProps & React.RefAttributes<InputRef>>> {
  Password: typeof PasswordMemo;
  TextArea: typeof TextAreaMemo;
  Otp: typeof OtpMemo;
}

export const Input = InternalInputMemo as InputType;
Input.Password = PasswordMemo;
Input.TextArea = TextAreaMemo;
Input.Otp = OtpMemo;

export interface InputFieldType
  extends React.ForwardRefExoticComponent<InputFieldProps & _InputProps & React.RefAttributes<InputRef>> {
  Password: typeof PasswordFiled;
  TextArea: typeof TextAreaField;
  Otp: typeof OtpField;
}

export const InputField = InternalInputField as InputFieldType;
InputField.Password = PasswordFiled;
InputField.TextArea = TextAreaField;
InputField.Otp = OtpField;

export const InputWrapper = makeField<_InputProps>(Input);
export const InputPasswordWrapper = makeField<_PasswordProps>(Input.Password);
export const TextAreaWrapper = makeField<_TextAreaProps>(Input.TextArea);
export const OtpWrapper = makeField<OTPPropsAntd>(Input.Otp);
